import { useEffect, useRef, useState } from 'react';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Modal } from '../../components/Modal';
import { newIdempotencyKey } from '../../lib/api';
import { describeApiError } from '../qrs/errors';
import { exclusionText } from '../qrs/states';
import type { Kit } from '../qrs/types';
import { useOrgApi } from '../qrs/useOrgApi';
import { describeStyle } from '../style/presets';
import type { QrStyle } from '../style/types';
import { DISTANCES } from './lotUtils';


export interface KitModalProps {
  open: boolean;
  onClose: () => void;
  /** Kit de un lote completo (solo los imprimibles) o de códigos puntuales (R7.5). */
  lotId?: string;
  lotLabel?: string;
  codes?: string[];
  domain?: string;
  style?: QrStyle | null;
  pollMs?: number;
}

const ERROR_TEXT: Record<string, string> = {
  no_printable: 'No hay QR imprimibles en esta selección (los dados de baja no se imprimen).',
  unreadable: 'Algunos QR no pasaron la verificación de lectura; no entregamos un archivo con QR ilegibles.',
  decoder_unavailable: 'El verificador de lectura no está disponible. Intenta de nuevo en unos minutos.',
  invalid_distance: 'La distancia de lectura no es válida.',
  seal_too_large: 'A esa distancia el QR no cabe en la hoja. Elige una distancia menor.',
  invalid_logo: 'El logo del partner no se pudo leer. Súbelo de nuevo en la página del partner.',
  style_rejected: 'El estilo del lote no cumple las reglas de lectura. Cámbialo a Clásico o ajústalo.',
  enqueue_failed: 'No pudimos iniciar la generación del kit. Intenta de nuevo.',
};

export function KitModal({ open, onClose, lotId, lotLabel, codes, domain, style, pollMs = 2000 }: KitModalProps) {
  const client = useOrgApi();
  const [distance, setDistance] = useState(30);
  const [kit, setKit] = useState<Kit | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => {
    if (!open) {
      clearTimeout(timer.current);
      setKit(null);
      setError(null);
    }
  }, [open]);

  function poll(kitId: string) {
    timer.current = setTimeout(async () => {
      try {
        const next = await client.get<Kit>(`/v1/kits/${encodeURIComponent(kitId)}`);
        setKit(next);
        if (next.status === 'pending' || next.status === 'running') poll(kitId);
      } catch (err) {
        setError(describeApiError(err, 'Perdimos el estado del kit. Vuelve a abrirlo en unos segundos.'));
      }
    }, pollMs);
  }

  async function request() {
    setError(null);
    setSubmitting(true);
    try {
      const body = lotId ? { lotId, readingDistanceCm: distance } : { codes, domain, readingDistanceCm: distance };
      const created = await client.post<Kit>('/v1/kits', body, { idempotencyKey: newIdempotencyKey() });
      setKit({ ...created, status: created.status ?? 'pending' });
      if (created.status === 'pending' || created.status === 'running') poll(created.id);
    } catch (err) {
      setError(describeApiError(err, 'No pudimos pedir el kit.'));
    } finally {
      setSubmitting(false);
    }
  }

  const busy = kit && (kit.status === 'pending' || kit.status === 'running');
  const download = kit?.status === 'ready' ? (kit.downloadUrl ?? null) : null;
  const scope = lotId ? `todos los imprimibles del lote ${lotLabel ?? lotId}` : `${codes?.length ?? 0} código(s): ${(codes ?? []).join(', ')}`;
  const styled = Boolean(style);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={`Kit de impresión · ${lotLabel ?? lotId ?? codes?.join(', ') ?? ''}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cerrar
          </Button>
          {download ? (
            <a href={download} target="_blank" rel="noopener" className="inline-flex items-center rounded-md bg-action px-4 py-2 text-sm font-semibold text-ink no-underline hover:bg-action-hover">
              Descargar PDF
            </a>
          ) : (
            <Button onClick={() => void request()} loading={submitting || Boolean(busy)} disabled={Boolean(busy)}>
              Generar kit
            </Button>
          )}
        </>
      }
    >
      <div className="grid gap-5 md:grid-cols-2">
        <div className="space-y-3">
          <div className="flex flex-col gap-1">
            <label htmlFor="kit-distance" className="text-xs font-medium text-ink-muted">
              Distancia de lectura
            </label>
            <select id="kit-distance" value={distance} disabled={Boolean(kit)} onChange={(e) => setDistance(Number(e.target.value))} className="w-full rounded-sm border border-line bg-white px-3 py-2 text-sm">
              {DISTANCES.map((d) => (
                <option key={d.cm} value={d.cm}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>
          <p className="text-sm text-ink">Incluye {scope}. El código y el estado de cada QR no cambian.</p>
          <p className="text-xs text-ink-muted">
            Contenido exacto <span className="font-mono">HTTPS://Q-R.CL/{'{CODE}'}</span>. La marca del partner va en el marco, fuera de la zona de silencio.
          </p>
          <p className="rounded-md bg-gray-50 p-2 text-xs text-gray-700">
            <b>Estilo: {describeStyle(style)}.</b> {styled ? 'Un QR con estilo se imprime como imagen de 300 dpi dentro del PDF.' : 'El estilo Clásico sale vectorial.'}
          </p>
        </div>
        <div className="flex flex-col items-center gap-2 rounded-md border-2 border-dashed border-gray-300 p-4" aria-hidden="true">
          <div className="text-xs text-ink-muted">Vista previa de una pieza</div>
          <div className="h-32 w-32 rounded bg-[repeating-conic-gradient(#16181D_0_25%,#fff_0_50%)] bg-[length:16px_16px]" />
          <div className="font-mono font-semibold tracking-widest">{codes?.[0] ?? 'K7MX'}</div>
          <div className="text-xs">Escanea y mira el menú</div>
        </div>
      </div>

      <div aria-live="polite" className="space-y-2">
        {busy && (
          <div className="space-y-2">
            <div className="h-2 overflow-hidden rounded-full bg-gray-200">
              <div className={`h-2 rounded-full bg-action ${kit?.status === 'running' ? 'w-2/3' : 'w-1/4'} transition-all motion-reduce:transition-none`} />
            </div>
            <p className="text-sm text-ink">{kit?.status === 'running' ? 'Generando el PDF y verificando la lectura de cada QR…' : 'Kit en cola. Empieza en unos segundos.'}</p>
          </div>
        )}
        {kit?.status === 'ready' && (
          <Banner tone="success">
            Kit listo{kit.itemCount ? ` · ${kit.itemCount.toLocaleString('es-CL')} QR` : ''}{kit.pageCount ? ` en ${kit.pageCount} páginas` : ''}. Lectura verificada. El enlace de descarga vence en 15 minutos.
          </Banner>
        )}
        {kit?.status === 'expired' && <Banner tone="warning">El archivo de este kit ya venció (se guarda 30 días). Genera uno nuevo.</Banner>}
        {kit?.status === 'failed' && (
          <Banner tone="error">{(kit.error?.code && ERROR_TEXT[kit.error.code]) ?? kit.error?.message ?? 'No pudimos generar el kit.'}</Banner>
        )}
        {kit?.failedCodes && kit.failedCodes.length > 0 && (
          <div className="rounded-md border border-error p-2 text-xs text-error-text">
            <b>QR ilegibles:</b> <span className="font-mono">{kit.failedCodes.join(', ')}</span>
          </div>
        )}
        {kit?.excludedCodes && kit.excludedCodes.length > 0 && (
          <p className="text-xs text-ink-muted">Excluidos: {kit.excludedCodes.map(exclusionText).join(' · ')}</p>
        )}
        {error && <Banner tone="error">{error}</Banner>}
      </div>
    </Modal>
  );
}
